import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IActivityLocation, IActivityLocationSearchRequest, ICountResponse, IPhoto } from '@curbnturf/entities';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'activity-location';

/** Columns `GET activity-location/paginated` sorts by. */
export type AdminPoiSort = 'id' | 'created' | 'name';

export interface IAdminPoiPage {
  total: number;
  page: number;
  pois: IActivityLocation[];
}

/**
 * POI (activity-location) admin calls: search with approval/hidden/deleted
 * filters, create/update (an admin update may set `available` = approved),
 * delete/restore, images. Replaces @curbnturf/activity's ActivityService +
 * NgRx facade for admin. Same endpoints cnt-landing's ActivityApiService uses.
 */
@Injectable({ providedIn: 'root' })
export class AdminPoiApiService {
  private readonly http = inject(HttpClient);

  /** Every matching POI, unpaged — for the CSV export. `deleted: true` here means deleted or not. */
  search(query: IActivityLocationSearchRequest): Observable<IActivityLocation[]> {
    return this.http.get<IActivityLocation[]>(`${API_URL}?${poiQueryString(query)}`);
  }

  /**
   * One page of matching POIs, sorted on the server (`GET activity-location/paginated`, admin only).
   * `deleted: true` means only deleted POIs.
   */
  page(
    query: IActivityLocationSearchRequest,
    page: number,
    pageSize: number,
    sort: AdminPoiSort = 'id',
    dir: 'asc' | 'desc' = 'desc',
  ): Observable<IAdminPoiPage> {
    const params = new URLSearchParams(poiQueryString(query));
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    params.set('sort', sort);
    params.set('dir', dir);
    return this.http.get<IAdminPoiPage>(`${API_URL}/paginated?${params}`);
  }

  /** POIs awaiting approval: not available, not deleted. */
  pendingCount(): Observable<ICountResponse> {
    return this.http.get<ICountResponse>(`${API_URL}/pending/count`);
  }

  fetch(id: number): Observable<IActivityLocation> {
    return this.http.get<IActivityLocation>(`${API_URL}/${id}`);
  }

  create(poi: IActivityLocation): Observable<IActivityLocation> {
    return this.http.post<IActivityLocation>(API_URL, poi);
  }

  update(poi: IActivityLocation): Observable<IActivityLocation> {
    if (!poi.id) {
      throw new Error('Missing POI Identifier');
    }
    return this.http.put<IActivityLocation>(`${API_URL}/${poi.id}`, poi);
  }

  remove(id: number): Observable<IActivityLocation> {
    return this.http.delete<IActivityLocation>(`${API_URL}/${id}`);
  }

  restore(id: number): Observable<IActivityLocation> {
    return this.http.post<IActivityLocation>(`${API_URL}/${id}/restore`, null);
  }

  uploadImage(id: number, formData: FormData): Observable<{ activityId: number; photos: IPhoto[] }> {
    return this.http.post<{ activityId: number; photos: IPhoto[] }>(`${API_URL}/${id}/upload-image`, formData);
  }

  removeImage(id: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${id}/remove-image/${photoIndex}`);
  }

  /** Removes the photo with this key; 404 if it's no longer there. Prefer this to removeImage's position. */
  removeImageByKey(id: number, key: string): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${id}/remove-image`, { params: { key } });
  }
}

/** Mirrors the server's `GET /activity-location` query names (selectedDump, activities=a,b, …). */
export function poiQueryString(query: IActivityLocationSearchRequest): string {
  const params = new URLSearchParams();
  const selectedKeys: Record<string, string> = {
    dump: 'selectedDump',
    restArea: 'selectedRestArea',
    wifi: 'selectedWifi',
    potableWater: 'selectedPotableWater',
    propane: 'selectedPropane',
  };
  for (const [key, param] of Object.entries(selectedKeys)) {
    const value = query.selected?.[key as keyof typeof query.selected];
    if (value) params.set(param, String(value));
  }
  if (query.activities?.length) params.set('activities', query.activities.join(','));
  if (query.searchAsAnd) params.set('searchAsAnd', 'true');
  if (query.userId) params.set('userId', String(query.userId));
  for (const key of ['available', 'hidden', 'deleted'] as const) {
    if (typeof query[key] === 'boolean') params.set(key, String(query[key]));
  }
  return params.toString();
}
